import React, { useState, useContext, useMemo, useEffect, useCallback } from "react";
import ToastContext from "./ToastContext";
import { ToastContainer } from "react-bootstrap";
import "./toast.css";
import userService from "../../Services/userService";
import UserContext from "../UserContext/UserContext";

import { useNavigate } from "react-router-dom";
import SubjectsContext from "../SubjectsContext/SubjectsContext";
import ToastItem from "./ToastItem";
import ErrorBoundary from "../../Components/ErrorBoundary/ErrorBoundary";
import { isSystemUseNotificationRequired } from "../../Components/SystemUseNotification/systemUseGate";

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const { userData, accessToken, imageSasToken, userPermissions } =
    useContext(UserContext);
  const [acknowledgedUserId, setAcknowledgedUserId] = useState(null);
  const sessionUserId = userData?.userInfo?.id || null;
  const hasAcknowledgedSystemUse =
    !!sessionUserId && acknowledgedUserId === sessionUserId;
  const setIsSystemUseAcknowledged = useCallback(
    (acknowledged) =>
      setAcknowledgedUserId(acknowledged ? sessionUserId : null),
    [sessionUserId],
  );
  const isSystemUseAcknowledged =
    hasAcknowledgedSystemUse ||
    !isSystemUseNotificationRequired(userData, userPermissions);
  const [startedResponding, setStartedResponding] = useState(false);
  const { subjects } = useContext(SubjectsContext);
  const navigate = useNavigate();

  const addToast = useCallback((
    title,
    category = "default",
    variant = "primary",
    documentInfo = null,
    setIsCriticalSoundPlaying = null,
    oldDocumentInfo = null,
    currentSound = null,
  ) => {
    setToasts((prevState) => [
      ...prevState,
      {
        id: `${Date.now()}-${Math.random()}`,
        title,
        category,
        variant,
        documentInfo,
        setIsCriticalSoundPlaying,
        oldDocumentInfo,
        currentSound,
      },
    ]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prevState) => {
      const toastToRemove = prevState.find((toast) => toast.id === id);
      const updatedToasts = prevState.filter((toast) => toast.id !== id);

      const wasCritical = toastToRemove?.category === "Critical";

      const remainingCriticalToasts = updatedToasts.filter(
        (t) => t.category === "Critical" && t.currentSound,
      );

      if (wasCritical && remainingCriticalToasts.length === 0) {
        if (toastToRemove.setIsCriticalSoundPlaying !== null) {
          toastToRemove.setIsCriticalSoundPlaying(false);
        }
        toastToRemove.currentSound?.stop();
      }

      return updatedToasts;
    });
  }, []);

  const respondingButton = useCallback((subject, index) => {
    setStartedResponding(true);
    const payload = {
      entityId: userData?.userInfo?.entityId,
      subjectId: subject.id,
      action: "Responded",
      alertId: subject.alertId,
    };

    if (userData?.userInfo?.firstName) {
      payload.firstName = userData.userInfo.firstName;
    }

    if (userData?.userInfo?.lastName) {
      payload.lastName = userData.userInfo.lastName;
    }

    if (userData?.userInfo?.title) {
      payload.title = userData.userInfo.title;
    }

    userService
      .logUsersAlertAction(payload, accessToken)
      .then(() => {
        removeToast(index);
        setStartedResponding(false);
      })
      .catch((error) => {
        console.error("Failed to log responding action:", error);
        setStartedResponding(false);
      });
  }, [userData?.userInfo?.entityId, userData?.userInfo?.firstName, userData?.userInfo?.lastName, userData?.userInfo?.title, accessToken, removeToast]);

  useEffect(() => {
    const currentUserId = userData?.userInfo?.id;

    setToasts((prevToasts) => {
      const removed = [];

      const updatedToasts = prevToasts.filter((toast) => {
        if (toast.category !== "Critical") return true;

        const subject = subjects?.find((s) => s.id === toast.documentInfo?.id);
        if (!subject || subject.wellnessCategory !== "High") {
          removed.push(toast);
          return false;
        }

        const userHasResponded = subject?.officersRespondedToCritical?.some(
          (officer) => officer.user === currentUserId,
        );
        if (userHasResponded) {
          removed.push(toast);
          return false;
        }

        return true;
      });

      if (removed.length === 0) return prevToasts;

      const remainingCriticalToasts = updatedToasts.filter(
        (t) => t.category === "Critical",
      );

      if (remainingCriticalToasts.length === 0) {
        removed.forEach((toast) => toast.currentSound?.stop());
      }

      return updatedToasts;
    });
  }, [subjects, userData]);

  const busyButton = useCallback((subject, index) => {
    setStartedResponding(true);
    const payload = {
      entityId: userData?.userInfo?.entityId,
      subjectId: subject.id,
      action: "Busy",
      alertId: subject.alertId,
    };
    userService
      .logUsersAlertAction(payload, accessToken)
      .then(() => {
        removeToast(index);
        setStartedResponding(false);
      })
      .catch((error) => {
        console.error("Failed to log busy action:", error);
        setStartedResponding(false);
      });
  }, [userData?.userInfo?.entityId, accessToken, removeToast]);

  const hasCriticalToast = toasts.some((toast) => toast.category === "Critical");
  const hasActiveCriticalSubject =
    subjects?.some((subject) => subject?.wellnessCategory === "High") ?? false;
  const hasCriticalAlert = hasCriticalToast || hasActiveCriticalSubject;

  const contextValue = useMemo(() => ({
    addToast,
    removeToast,
    setIsSystemUseAcknowledged,
    isSystemUseAcknowledged,
    hasCriticalAlert,
  }), [addToast, removeToast, setIsSystemUseAcknowledged, isSystemUseAcknowledged, hasCriticalAlert]);

  const onDeviceToastCloseClicked = useCallback((e, id) => {
    e.stopPropagation();

    setToasts((prevToasts) => {
      const toastBeingClosed = prevToasts.find((toast) => toast.id === id);

      const updatedToasts = prevToasts.filter((toast) => toast.id !== id);

      if (toastBeingClosed && toastBeingClosed.category !== "Critical") {
        const nonCriticalRemaining = updatedToasts.filter(
          (t) =>
            t.category !== "Critical" &&
            t.documentInfo.priorityValue !== 36 &&
            t.documentInfo.priorityValue !== 195,
        );

        if (nonCriticalRemaining.length === 0) {
          toastBeingClosed.currentSound?.stop();
        }
      }

      return updatedToasts;
    });
  }, []);

  const subjectsToReopen = useMemo(() => {
    return subjects?.filter((subject) => {
      const officers = subject?.officersRespondedToCritical || [];
      const hasUserResponded = officers?.some(
        (officer) => officer?.user === userData?.userInfo?.id,
      );
      return subject?.wellnessCategory === "High" && !hasUserResponded;
    });
  }, [subjects, userData]);

  const reopenCriticalAlerts = useCallback(() => {
    if (subjectsToReopen?.length) {
      subjectsToReopen.forEach((subject) => {
        addToast("Critical Alert", "Critical", "danger", subject);
      });
    }
  }, [subjectsToReopen, addToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {hasCriticalToast && (
        <div
          className="blur-overlay"
          style={{
            display:
              isSystemUseAcknowledged && !userData?.userInfo?.disableAlertPopups
                ? null
                : "none",
          }}
        ></div>
      )}
      <ToastContainer
        position="top-end"
        style={{
          marginTop: 85,
          marginRight: 12,
          display: isSystemUseAcknowledged ? null : "none",
        }}
      >
        {toasts.map((toast) => (
          <ErrorBoundary
            key={toast.id}
            variant="item"
            label={toast.category === "Critical" ? "A critical alert" : "An alert"}
            onDismiss={() => removeToast(toast.id)}
          >
            <ToastItem
              toast={toast}
              userData={userData}
              imageSasToken={imageSasToken}
              startedResponding={startedResponding}
              respondingButton={respondingButton}
              busyButton={busyButton}
              removeToast={removeToast}
              onClose={onDeviceToastCloseClicked}
              navigate={navigate}
            />
          </ErrorBoundary>
        ))}
      </ToastContainer>
      {!userData?.userInfo?.disableAlertPopups &&
        subjectsToReopen?.length > 0 &&
        userData?.userInfo?.userRole !== "4SightAdmin" &&
        isSystemUseAcknowledged && (
          <div
            className="toast-critical-realert-container"
            onClick={reopenCriticalAlerts}
          >
            <div className="toast-critical-realert-circle-container">
              {subjectsToReopen?.length}
            </div>
          </div>
        )}
    </ToastContext.Provider>
  );
};
